import { useCallback, useEffect, useRef, useState } from "react";
import { LogOut, Users, CalendarDays, Download, Megaphone, Inbox } from "lucide-react";
import { toast } from "sonner";
import {
  scoutLogout,
  scoutMe,
  scoutSchedule,
  listRuns,
  getRun,
  listCandidates,
  downloadCsv,
  listQuestions,
  STATUSES,
} from "@/lib/scoutApi";
import ScoutLogin from "@/components/scout/ScoutLogin";
import EthosEditor from "@/components/scout/EthosEditor";
import RunScout from "@/components/scout/RunScout";
import CandidateCard from "@/components/scout/CandidateCard";
import EpisodeCalendar from "@/components/scout/EpisodeCalendar";
import DigestPreview from "@/components/scout/DigestPreview";
import TrendingNews from "@/components/scout/TrendingNews";
import RotationPreview from "@/components/scout/RotationPreview";
import WeeklyRecap from "@/components/scout/WeeklyRecap";
import PostsSheet from "@/components/scout/PostsSheet";
import BalanceMeter from "@/components/scout/BalanceMeter";
import QuestionInbox from "@/components/scout/QuestionInbox";
import { useAuth } from "@/lib/auth";
import ScoutInstall from "@/components/scout/ScoutInstall";
import ScoutNotifications from "@/components/scout/ScoutNotifications";
import SuggestGuest from "@/components/scout/SuggestGuest";
import { LaneChips } from "@/components/scout/LaneChips";
import AllowListPanel from "@/components/scout/AllowListPanel";

const ScoutDashboard = ({ onLogout }) => {
  const [candidates, setCandidates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("crm");
  const [openQuestions, setOpenQuestions] = useState(null);
  const openInbox = useCallback(() => setView("inbox"), []);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    const [c, r] = await Promise.all([listCandidates(), listRuns()]);
    setCandidates(c);
    setRuns(r);
  }, []);

  useEffect(() => {
    refresh();
    scoutSchedule()
      .then(setSchedule)
      .catch(() => {});
    listQuestions()
      .then((d) => setOpenQuestions(d.unhandled))
      .catch(() => {});
  }, [refresh]);

  const activeRun = runs.find((r) => r.status === "running");

  useEffect(() => {
    if (!activeRun) return undefined;
    pollRef.current = setInterval(async () => {
      const r = await getRun(activeRun.id);
      if (r.status !== "running") {
        clearInterval(pollRef.current);
        await refresh();
        if (r.status === "done")
          toast.success(`Scout found ${r.candidate_count} new candidates`);
        else toast.error("Scout run failed", { description: r.error });
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeRun, refresh]);

  const onStarted = (run) => setRuns((rs) => [run, ...rs]);
  const onChange = (u) =>
    setCandidates((cs) => cs.map((c) => (c.id === u.id ? u : c)));
  const onDelete = (id) => setCandidates((cs) => cs.filter((c) => c.id !== id));
  const visible =
    filter === "all"
      ? candidates
      : filter === "adversarial"
        ? candidates.filter((c) => c.adversarial)
        : candidates.filter((c) => c.status === filter);
  const lastRun = runs.find((r) => r.status !== "running");

  return (
    <main
      data-testid="scout-dashboard"
      className="min-h-screen bg-white text-[#0A192F]"
    >
      <div className="px-6 md:px-12 lg:px-20 py-12 max-w-[1400px] mx-auto">
        <header className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs tracking-tight text-[var(--accent)] mb-2">
              ATFQ · Private
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
              Guest Scout.
            </h1>
            <p className="mt-2 text-sm text-slate-500 max-w-xl">
              An agent that scours the web for candid, reachable guests who fit
              ATFQ — then drafts the ask.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <ScoutNotifications onOpenInbox={openInbox} />
            <ScoutInstall />
            <button
              type="button"
              data-testid="scout-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0A192F]"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </header>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-10">
            <RunScout
              activeRun={activeRun}
              onStarted={onStarted}
              schedule={schedule}
            />
            {lastRun && (
              <div
                data-testid="scout-last-run"
                className="text-sm text-slate-600 border-l-2 border-[var(--accent)] pl-4"
              >
                <div className="text-xs text-slate-500 mb-1">
                  Last run · {lastRun.triggered_by} · {lastRun.status} ·{" "}
                  {lastRun.candidate_count} found
                </div>
                <LaneChips lanes={lastRun.lanes} />
                {lastRun.summary || lastRun.error}
              </div>
            )}
            <SuggestGuest onCreated={(c) => setCandidates((cs) => [c, ...cs])} />
            <BalanceMeter refreshKey={candidates.map((c) => `${c.id}:${c.status}:${c.featured ? 1 : 0}`).join()} />
            <RotationPreview refreshKey={candidates.map((c) => `${c.id}:${c.featured ? 1 : 0}`).join()} />
            <WeeklyRecap refreshKey={candidates.map((c) => `${c.id}:${c.status}`).join()} />
            <TrendingNews schedule={schedule} />
            <EthosEditor />
            <AllowListPanel />
            <DigestPreview />
          </div>

          <div className="lg:col-span-7">
            <div className="flex gap-1 mb-6" data-testid="scout-view-toggle">
              <button
                type="button"
                data-testid="scout-view-crm"
                onClick={() => setView("crm")}
                aria-pressed={view === "crm"}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${view === "crm" ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"}`}
              >
                <Users className="w-3.5 h-3.5" /> Guest CRM ·{" "}
                {candidates.length}
              </button>
              <button
                type="button"
                data-testid="scout-view-calendar"
                onClick={() => setView("calendar")}
                aria-pressed={view === "calendar"}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${view === "calendar" ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"}`}
              >
                <CalendarDays className="w-3.5 h-3.5" /> Calendar ·{" "}
                {candidates.filter((c) => c.status === "booked").length}
              </button>
              <button
                type="button"
                data-testid="scout-view-posts"
                onClick={() => setView("posts")}
                aria-pressed={view === "posts"}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${view === "posts" ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"}`}
              >
                <Megaphone className="w-3.5 h-3.5" /> Posts
              </button>
              <button
                type="button"
                data-testid="scout-view-inbox"
                onClick={() => setView("inbox")}
                aria-pressed={view === "inbox"}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${view === "inbox" ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"}`}
              >
                <Inbox className="w-3.5 h-3.5" /> Inbox
                {openQuestions ? <span data-testid="scout-inbox-count" className="font-mono">· {openQuestions}</span> : null}
              </button>
              <button
                type="button"
                data-testid="scout-export-csv"
                onClick={() => downloadCsv().catch(() => toast.error("Export failed"))}
                className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 text-slate-500 hover:text-[#0A192F] hover:border-[#0A192F] transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            </div>
            {view === "calendar" ? (
              <EpisodeCalendar candidates={candidates} onChange={onChange} />
            ) : view === "posts" ? (
              <PostsSheet candidates={candidates} onChange={onChange} onRefresh={refresh} />
            ) : view === "inbox" ? (
              <QuestionInbox onCount={setOpenQuestions} onPrepAdded={() => refresh()} />
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-end gap-4 mb-6">
                  <div
                    className="flex flex-wrap gap-1"
                    data-testid="scout-status-filter"
                  >
                    {["all", "adversarial", ...STATUSES].map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`scout-filter-${s}`}
                        onClick={() => setFilter(s)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${filter === s ? "bg-[#0A192F] border-[#0A192F] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                {visible.length === 0 ? (
                  <p
                    data-testid="scout-empty"
                    className="text-sm text-slate-500 border-t border-slate-200 pt-8"
                  >
                    No candidates here yet. Dispatch the scout or wait for the
                    Tue/Wed 6:30 AM run.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {visible.map((c) => (
                      <CandidateCard
                        key={c.id}
                        c={c}
                        onChange={onChange}
                        onDelete={onDelete}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default function ScoutPage() {
  const [authed, setAuthed] = useState(null);
  const { user, loading, logout } = useAuth();

  useEffect(() => {
    if (loading) return;
    scoutMe()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, [loading, user]);

  if (authed === null) return null;
  if (!authed) return <ScoutLogin onSuccess={() => setAuthed(true)} />;
  return (
    <ScoutDashboard
      onLogout={async () => {
        await scoutLogout().catch(() => {});
        if (user) await logout();
        setAuthed(false);
      }}
    />
  );
}
